// labelUtils.js
import * as turf from "@turf/turf";
import { formatArea } from "./formatting";
import { getVertexLabel } from "./helpers";
import { addLayerIfNotExists, addOrUpdateSource, removeLayerAndSource } from "./map-layer";

/**
 * Creates an array of distance label features and vertex label features for a given polygon.
 * @param {object} polygon - A GeoJSON Feature (Polygon)
 * @returns {{ distanceFeatures: object[], vertexFeatures: object[] }}
 */
export function createDistanceAndVertexFeatures(polygon) {
  const distanceFeatures = [];
  const vertexFeatures = [];

  if (!polygon || polygon.geometry.type !== "Polygon") return { distanceFeatures, vertexFeatures };

  const coords = polygon.geometry.coordinates[0];
  for (let i = 0; i < coords.length - 1; i++) {
    const from = turf.point(coords[i]);
    const to = turf.point(coords[i + 1]);
    const distance = turf.distance(from, to, { units: "meters" });
    const midpoint = turf.midpoint(from, to);

    distanceFeatures.push({
      type: "Feature",
      geometry: midpoint.geometry,
      properties: { distance: `${distance.toFixed(2)} m` },
    });

    const label = getVertexLabel(i); // 'A', 'B', etc.
    vertexFeatures.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: coords[i] },
      properties: { label: label.toUpperCase() },
    });
  }

  return { distanceFeatures, vertexFeatures };
}

/**
 * Adds or updates the distance labels layer.
 * @param {mapboxgl.Map} map
 * @param {object[]} distanceFeatures - array of line midpoint features
 */
export function addDistanceLabels(map, distanceFeatures) {
  const sourceId = "distance-labels-property";
  addOrUpdateSource(map, sourceId, {
    type: "FeatureCollection",
    features: distanceFeatures,
  });
  addLayerIfNotExists(map, {
    id: sourceId,
    type: "symbol",
    source: sourceId,
    layout: {
      "text-field": ["get", "distance"],
      "text-size": 14,
      "text-anchor": "top",
      "text-offset": [0, 0.5],
      'text-letter-spacing': 0.1,
    },
    paint: {
      "text-color": "#000",
      "text-halo-color": "#fff",
      "text-halo-width": 1,
    },
  });
}

/**
 * Adds or updates the vertex labels layer.
 * @param {mapboxgl.Map} map
 * @param {object[]} vertexFeatures
 */
export function addVertexLabels(map, vertexFeatures) {
  const sourceId = "vertex-labels-property";
  addOrUpdateSource(map, sourceId, {
    type: "FeatureCollection",
    features: vertexFeatures,
  });
  addLayerIfNotExists(map, {
    id: sourceId,
    type: "symbol",
    source: sourceId,
    layout: {
      "text-field": ["get", "label"],
      "text-size": 14,
      "text-offset": [0, -1],
      "text-anchor": "bottom",
    },
    paint: {
      "text-color": "#343745",
      "text-halo-color": "#ffffff",
      "text-halo-width": 1,
      "text-background-color": "#ffffff",
      "text-background-padding": [2, 2],
    },
  });
}

/**
 * Adds or updates the area label for a given polygon.
 * @param {mapboxgl.Map} map
 * @param {object} polygon - A GeoJSON Feature (Polygon)
 */
export function addAreaLabel(map, polygon) {
  const area = turf.area(polygon);
  const centroid = turf.centroid(polygon);
  const feature = {
    type: "Feature",
    geometry: centroid.geometry,
    properties: { area: formatArea(area) },
  };

  const sourceId = "area-labels-property";
  addOrUpdateSource(map, sourceId, {
    type: "FeatureCollection",
    features: [feature],
  });
  addLayerIfNotExists(map, {
    id: sourceId,
    type: "symbol",
    source: sourceId,
    layout: {
      "text-field": ["get", "area"],
      "text-size": 14,
      "text-anchor": "center",
    },
    paint: {
      "text-color": "#fff",
    },
  });
}

/**
 * Removes distance, vertex, and area label layers (and their sources).
 * @param {mapboxgl.Map} map
 */
export function removeAllLabels(map) {
  removeLayerAndSource(map, "distance-labels-property", "distance-labels-property");
  removeLayerAndSource(map, "vertex-labels-property", "vertex-labels-property");
  removeLayerAndSource(map, "area-labels-property", "area-labels-property");
}


/**
 * Adds circle markers for all vertices (coordinates) of a Polygon/MultiPolygon
 * using turf.explode, into a single circle layer.
 *
 * @param {mapboxgl.Map} map - The Mapbox GL JS map instance
 * @param {object} polygonFeature - A GeoJSON Feature (Polygon or MultiPolygon)
 * @param {string} sourceId - Unique ID to register the new GeoJSON source
 * @param {string} layerId - Unique ID to register the new circle layer
 * @param {boolean} removeDuplicates - Whether to remove repeated vertices (e.g. closed ring duplicates)
 */
export function addPolygonVerticesAsCircles(map, polygonFeature, sourceId, layerId, removeDuplicates = false) {
  // 1. Explode the polygon into individual points
  let explodedFC = turf.explode(polygonFeature);
  // explodedFC is a FeatureCollection of Points

  // 2. (Optional) Remove duplicate points
  // By default, ring closing coordinates appear twice in polygons. 
  // If you want each vertex only once:
/*   if (removeDuplicates) {
    explodedFC = turf.uniq(explodedFC);
    // uniq will filter out duplicates based on coordinate equality
  } */

  // 3. Add or update a GeoJSON source for these points
  addOrUpdateSource(map, sourceId, explodedFC);

  // 4. Add or update a circle layer  
  addLayerIfNotExists(map, {
    id: layerId,
    type: 'circle',
    source: sourceId,
    paint: {
      'circle-radius': 5,
      'circle-color': '#5E6278',
      'circle-stroke-width': 2,
      'circle-stroke-color': '#fff',
    }
  });
}




/**
 * Calculates the centroid of a given GeoJSON feature and returns its coordinates.
 *
 * @param {Object} feature - A valid GeoJSON Feature.
 * @returns {number[]|null} - The coordinates of the centroid [longitude, latitude], or null if calculation fails.
 */
export function getCentroidCoordinates(feature) {
  if (!feature) return null;
  try {
    const centroid = turf.centroid(feature);
    return centroid?.geometry?.coordinates || null;
  } catch (error) {
    console.error("Error calculating centroid coordinates:", error);
    return null;
  }
}

/**
 * Reverse geocodes coordinates to an address using the Mapbox Geocoding API.
 *
 * @param {number[]} coordinates - An array containing [longitude, latitude].
 * @param {string} accessToken - Your Mapbox access token.
 * @returns {Promise<string|null>} - A promise that resolves to the address (place_name)
 *                                   if found, or null if no results are available.
 */
export async function reverseGeocode(coordinates) {
  
  if (!Array.isArray(coordinates) || coordinates.length !== 2) {
    throw new Error("Invalid coordinates. Expected an array [longitude, latitude].");
  }
  const [longitude, latitude] = coordinates;
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`;
  
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Mapbox API request failed with status: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    if (data?.features && data.features.length > 0) {
      return data.features[0].place_name;
    }
    return null;
  } catch (error) {
    console.error("Error in reverseGeocode:", error);
    throw error;
  }
}
